import { useEffect, useState } from 'react';
import type { Section } from '../../lib/admin-api';

export default function CourseOutline({ sections }: { sections: Section[] }) {
  const [active, setActive] = useState('');

  useEffect(() => {
    const rows = sections.flatMap((section, si) => section.modules.map((_, mi) => document.getElementById(`course-module-${si}-${mi}`))).filter((row): row is HTMLElement => !!row);
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-120px 0px -45% 0px' });
    rows.forEach(row => observer.observe(row));
    return () => observer.disconnect();
  }, [sections]);

  const contents = <nav aria-label="Оглавление курса" className="space-y-4 p-3">
    {sections.map((section, si) => <div key={section.id}>
      <div className="mb-1 px-2 text-sm font-semibold">{si + 1}. {section.name || 'Без названия'}</div>
      {section.modules.map((module, mi) => {
        const target = `course-module-${si}-${mi}`;
        const count = (module as typeof module & { questionsCount?: number }).questionsCount;
        return <a key={module.id} href={`#${target}`} onClick={() => setActive(target)} aria-current={active === target ? 'location' : undefined}
          className={`mb-1 block rounded-lg px-2 py-2 text-sm hover:bg-[#E5F2EF] ${active === target ? 'bg-[#E5F2EF] text-[#0F766E]' : 'text-[#252A2D]'}`}>
          <span className="block">{si + 1}.{mi + 1} {module.name || 'Без названия'}</span>
          <span className="mt-1 flex flex-wrap gap-x-3 text-xs text-[#626B73]">
            <span>📖 {module.lesson ? 'Урок есть' : 'Нет урока'}</span>
            <span>📋 {module.quizIds?.length ? 'Тест есть' : 'Нет теста'}</span>
            <span>{module.quizIds?.length ? (typeof count === 'number' ? `${count} вопросов` : 'Вопросы: нет данных') : '0 вопросов'}</span>
          </span>
        </a>;
      })}
      {!section.modules.length && <p className="px-2 text-xs text-[#626B73]">Нет модулей</p>}
    </div>)}
    {!sections.length && <p className="text-sm text-[#626B73]">Добавьте секцию, чтобы начать курс.</p>}
  </nav>;

  return <aside className="min-w-0 lg:sticky lg:top-28">
    <details className="rounded-xl border border-[#E1E5E3] bg-white lg:hidden">
      <summary className="cursor-pointer p-3 font-semibold text-[#0F766E]">Оглавление</summary>
      <div className="max-h-[50dvh] overflow-y-auto">{contents}</div>
    </details>
    <div className="hidden max-h-[calc(100dvh-8rem)] overflow-y-auto rounded-xl border border-[#E1E5E3] bg-white lg:block">
      <h2 className="sticky top-0 border-b border-[#E1E5E3] bg-white p-4 font-semibold">Оглавление курса</h2>
      {contents}
    </div>
  </aside>;
}
